import React, { useState, useEffect, useRef } from 'react';
import { useAppStore } from '../../store/useAppStore.ts';
import { Modal, ModalHeader } from './Modal.tsx';
import { Button } from './Button.tsx';
import { EmptyState } from './EmptyState.tsx';
import { Icon } from './Icon.tsx';
import { FileNode, type FilePolicy } from '../../features/files/types.ts';
import { showToast } from '../../utils/notifications.ts';
import { listPersonalFiles } from '../../features/files/utils/file-api.ts';
import { triggerScriptAnalysis } from '../../features/sync/ai-api.ts';
import { logger } from '../../utils/logger.ts';
import { logBetaAnalyticsEvent, toFileSizeBucket, toMimeCategory } from '@/utils/beta-analytics.ts';
import { createUploadSession, finalizeUpload, requestFileAccess, uploadToSession } from '../../features/files/service.ts';
import { projectWideAccessPolicy } from '../../features/files/policy.ts';
import { getEffectiveUserId } from '../../features/auth/current-user-identity.ts';
import { getFilesFromDataTransfer, toFileArray, type DroppedFileCollection } from './file-drop.ts';

interface UniversalFilePickerProps {
    isOpen: boolean;
    onClose: () => void;
    accept?: string; // MIME types (e.g., "image/*", "audio/*")
    mode?: 'single' | 'multiple';
    filter?: (file: FileNode) => boolean;
    onSelect: (files: FileNode[]) => void;
    onScriptUpload?: (file: { storagePath: string, name: string, jobId?: string }) => void; // Called after script upload completes (for job tracking)
    title?: string;
    personalMode?: boolean; // View only user's own files and upload as 'owner-only'
    scriptMode?: boolean; // Script import mode - uploads to /scripts/ to trigger Bucket Relay
    scriptImportMode?: 'replace' | 'append';
    uploadPolicy?: (ownerUserId: string) => FilePolicy;
}

type Tab = 'repository' | 'upload';
const NativeInput = 'input';

function getFileIcon(file: FileNode): string {
    if (file.mimeType?.startsWith('image/')) return 'image';
    if (file.mimeType?.startsWith('video/')) return 'videocam';
    if (file.mimeType?.startsWith('audio/')) return 'audiotrack';
    if (file.mimeType?.includes('pdf')) return 'picture_as_pdf';
    return 'description';
}

function getFileKindLabel(file: FileNode): string {
    if (file.mimeType?.startsWith('image/')) return 'Image';
    if (file.mimeType?.startsWith('video/')) return 'Video';
    if (file.mimeType?.startsWith('audio/')) return 'Audio';
    if (file.mimeType?.includes('pdf')) return 'PDF';
    const extension = file.name.split('.').pop();
    return extension && extension !== file.name ? extension.toUpperCase().slice(0, 8) : 'File';
}

function formatFileSize(bytes?: number): string {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

const MIME_TYPE_BY_EXTENSION: Record<string, string> = {
    gif: 'image/gif',
    jpeg: 'image/jpeg',
    jpg: 'image/jpeg',
    png: 'image/png',
    webp: 'image/webp',
};

function inferMimeTypeFromName(name: string): string | null {
    const extension = name.split('.').pop()?.toLowerCase();
    return extension ? MIME_TYPE_BY_EXTENSION[extension] ?? null : null;
}

function mimeTypeCandidates(file: FileNode): string[] {
    return [
        file.mimeType,
        inferMimeTypeFromName(file.name),
    ].filter((value): value is string => Boolean(value));
}

function fileMatchesAcceptType(file: FileNode, acceptType: string): boolean {
    const candidates = mimeTypeCandidates(file);
    if (acceptType.endsWith('/*')) {
        const prefix = acceptType.slice(0, -1);
        return candidates.some(candidate => candidate.startsWith(prefix));
    }
    return candidates.some(candidate => candidate === acceptType);
}

function fileMatchesAccept(file: FileNode, accept: string): boolean {
    if (accept === '*/*') return true;

    const acceptTypes = accept.split(',').map(t => t.trim()).filter(Boolean);
    return acceptTypes.some(acceptType => fileMatchesAcceptType(file, acceptType));
}

function stripLegacyFileUrls(file: FileNode): FileNode {
    const {
        url: _url,
        downloadUrl: _downloadUrl,
        ...sanitized
    } = file as FileNode & { downloadUrl?: unknown };
    return sanitized;
}

interface PickerFileItemProps {
    file: FileNode;
    isCached: boolean;
    isSelected: boolean;
    layout: 'grid' | 'list';
    onSelect: () => void;
}

const PickerFileItem: React.FC<PickerFileItemProps> = ({
    file,
    isCached,
    isSelected,
    layout,
    onSelect,
}) => {
    const isImageFile = file.mimeType?.startsWith('image/');
    const previewUrl = file.thumbnailUrl || (isImageFile ? file.accessUrl : undefined);
    const kindLabel = getFileKindLabel(file);
    const selectedClasses = isSelected
        ? 'border-primary bg-primary/10 ring-2 ring-primary/25'
        : 'border-gray-200 bg-white hover:border-primary/50 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800/70 dark:hover:bg-gray-800';

    if (layout === 'list') {
        return (
            <Button
                variant="ghost"
                className={`w-full justify-start rounded-lg border px-4 py-2 text-left ${selectedClasses}`}
                aria-pressed={isSelected}
                onClick={onSelect}
            >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-gray-200 bg-gray-100 dark:border-gray-700 dark:bg-gray-900">
                    {previewUrl ? (
                        <img src={previewUrl} alt="" width={40} height={40} className="h-full w-full rounded-md object-cover" />
                    ) : (
                        <Icon name={getFileIcon(file)} className="text-gray-500 dark:text-gray-400" />
                    )}
                </span>
                <span className="min-w-0 flex-1">
                    <span className="block truncate font-semibold text-gray-900 text-sm dark:text-white">
                        {file.name}
                    </span>
                    <span className="block truncate font-normal text-gray-500 text-xs dark:text-gray-400">
                        {[kindLabel, formatFileSize(file.size)].filter(Boolean).join(' - ')}
                    </span>
                </span>
                {isCached ? <Icon name="offline_pin" size="xs" className="text-green-500" /> : null}
                {isSelected ? <Icon name="check_circle" size="sm" className="text-primary" /> : null}
            </Button>
        );
    }

    return (
        <Button
            variant="ghost"
            className={`min-h-28 w-full flex-col items-stretch justify-start rounded-lg border p-3 text-left ${selectedClasses}`}
            aria-pressed={isSelected}
            onClick={onSelect}
        >
            <span className="relative flex h-16 w-full items-center justify-center overflow-hidden rounded-md border border-gray-200 bg-gray-100 dark:border-gray-700 dark:bg-gray-900">
                {previewUrl ? (
                    <img src={previewUrl} alt="" width={160} height={64} className="h-full w-full object-cover" />
                ) : (
                    <span className="flex flex-col items-center gap-1">
                        <Icon name={getFileIcon(file)} size="lg" className="text-gray-500 dark:text-gray-400" />
                        <span className="max-w-20 truncate font-semibold text-[10px] text-gray-500 uppercase dark:text-gray-400">
                            {kindLabel}
                        </span>
                    </span>
                )}
                {isSelected ? (
                    <span className="absolute top-1 right-1 rounded-full bg-primary p-1 text-white shadow-md">
                        <Icon name="check" size="xs" />
                    </span>
                ) : null}
            </span>
            <span className="mt-2 min-w-0">
                <span className="block truncate font-semibold text-gray-900 text-sm dark:text-white">
                    {file.name}
                </span>
                <span className="block truncate font-normal text-gray-500 text-xs dark:text-gray-400">
                    {[kindLabel, formatFileSize(file.size)].filter(Boolean).join(' - ')}
                </span>
            </span>
            {isCached ? (
                <span className="mt-1 inline-flex items-center gap-1 font-medium text-green-600 text-xs dark:text-green-400">
                    <Icon name="offline_pin" size="xs" />
                    Offline
                </span>
            ) : null}
        </Button>
    );
};

interface PickerUploadDropzoneProps {
    disabled: boolean;
    onFilesSelected: (files: DroppedFileCollection) => void;
}

const PickerUploadDropzone: React.FC<PickerUploadDropzoneProps> = ({ disabled, onFilesSelected }) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [dragOver, setDragOver] = useState(false);

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files.length > 0) {
            onFilesSelected(event.target.files);
        }
    };

    return (
        <>
            <NativeInput
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={handleFileSelect}
                disabled={disabled}
            />
            <Button
                className={`flex w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-8 text-center transition-all ${
                    dragOver
                        ? 'border-primary bg-primary/10'
                        : 'border-gray-300 hover:border-primary/50 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800'
                } ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
                onClick={() => {
                    if (!disabled) fileInputRef.current?.click();
                }}
                onDragLeave={(event) => {
                    event.preventDefault();
                    setDragOver(false);
                }}
                onDragOver={(event) => {
                    event.preventDefault();
                    setDragOver(true);
                }}
                onDrop={(event) => {
                    event.preventDefault();
                    setDragOver(false);
                    if (disabled) return;

                    const dataTransfer = event.dataTransfer;
                    void getFilesFromDataTransfer(dataTransfer).then((files) => {
                        if (files.length > 0) {
                            onFilesSelected(files);
                        }
                    });
                }}
                size="none"
                type="button"
                variant="plain"
            >
                <Icon
                    name="cloud_upload"
                    className="mx-auto mb-4 text-5xl text-gray-400 dark:text-gray-600"
                />
                <p className="mb-2 font-medium text-base text-gray-700 dark:text-gray-300">
                    Drop files here or <span className="text-primary">click to browse</span>
                </p>
                <p className="text-gray-500 text-sm dark:text-gray-400">
                    Maximum file size: 200MB
                </p>
            </Button>
        </>
    );
};

/**
 * UniversalFilePicker - Repository-first file selection component
 * 
 * Allows users to:
 * 1. Select existing files from the repository
 * 2. Upload new files to the repository
 * 
 * All uploads go through the repository for consistency and reusability.
 */
export const UniversalFilePicker: React.FC<UniversalFilePickerProps> = ({
    isOpen,
    onClose,
    accept = '*/*',
    mode = 'single',
    filter,
    onSelect,
    onScriptUpload,
    title = 'Select File',
    personalMode = false,
    scriptMode = false,
    scriptImportMode = 'replace',
    uploadPolicy
}) => {
    const orgId = useAppStore(state => state.cloud.orgId);
    const projectId = useAppStore(state => state.cloud.projectId);
    const files = useAppStore(state => state.files);
    const filesActions = useAppStore(state => state.filesActions);
    const currentFolderId = useAppStore(state => state.currentFolderId);
    const viewMode = useAppStore(state => state.viewMode);
    const cachedFileIds = useAppStore(state => state.cachedFileIds);
    const currentUser = useAppStore(state => state.auth.user);
    const appUserId = currentUser ? getEffectiveUserId() : undefined;

    const [activeTab, setActiveTab] = useState<Tab>('repository');
    const [selectedFileIds, setSelectedFileIds] = useState<string[]>([]);
    const [isUploading, setIsUploading] = useState(false);
    const [isResolvingSelection, setIsResolvingSelection] = useState(false);
    const [personalFiles, setPersonalFiles] = useState<FileNode[]>([]);

    const handleClose = () => {
        setSelectedFileIds([]);
        setActiveTab('repository');
        onClose();
    };

    // Load files when modal opens
    useEffect(() => {
        if (isOpen && orgId && projectId) {
            const scope = { orgId, projectId };
            if (personalMode) {
                // Personal mode: load only user's own files
                const userId = appUserId;
                if (userId) {
                    listPersonalFiles(scope, userId)
                        .then(setPersonalFiles)
                        .catch(err => {
                            console.error('Failed to load personal files:', err);
                            showToast('Failed to load files', 'error');
                        });
                }
            } else {
                // Normal mode: use store
                void filesActions.loadFiles(projectId, currentFolderId);
            }
        }
    }, [isOpen, orgId, projectId, currentFolderId, personalMode, filesActions, appUserId]);

    // Filter files based on accept prop and custom filter
    const sourceFiles = personalMode ? personalFiles : files;
    const filteredFiles = sourceFiles.filter(file => {
        if (file.type === 'folder') return false;
        
        if (!fileMatchesAccept(file, accept)) return false;

        // Apply custom filter
        if (filter && !filter (file)) return false;

        return true;
    });

    const handleFileSelect = (fileId: string) => {
        if (mode === 'single') {
            setSelectedFileIds([fileId]);
        } else {
            setSelectedFileIds(prev => 
                prev.includes(fileId) 
                    ? prev.filter(id => id !== fileId)
                    : [...prev, fileId]
            );
        }
    };

    const resolveFilesForSelection = async (selectedFiles: FileNode[]): Promise<FileNode[]> => {
        if (!orgId || !projectId) {
            return selectedFiles;
        }

        const scope = { orgId, projectId };
        return Promise.all(selectedFiles.map(async (file) => {
            if (file.type !== 'file') {
                return stripLegacyFileUrls(file);
            }

            const access = await requestFileAccess(scope, file.id, 'preview', 'universal-file-picker-select');
            return stripLegacyFileUrls({
                ...file,
                ...(access.file ?? {}),
                accessUrl: access.url,
            });
        }));
    };

    const handleConfirm = async () => {
        const selectedFiles = sourceFiles.filter(f => selectedFileIds.includes(f.id));
        setIsResolvingSelection(true);
        try {
            onSelect(await resolveFilesForSelection(selectedFiles));
            handleClose();
        } catch (error: unknown) {
            const msg = error instanceof Error ? error.message : 'Unknown error';
            logger.error('Failed to resolve file access for selection:', error);
            showToast(`Failed to access selected file: ${msg}`, 'error');
        } finally {
            setIsResolvingSelection(false);
        }
    };

    const handleUpload = async (fileList: DroppedFileCollection) => {
        if (!orgId || !projectId) {
            showToast('Please open a project first', 'error');
            return;
        }

        setIsUploading(true);
        const uploadedFiles: FileNode[] = [];
        const uploadFiles = toFileArray(fileList);

        try {
            const userId = appUserId;
            const scope = { orgId, projectId };
            
            for (const file of uploadFiles) {

                
                // Script mode uses specialized upload path to trigger Bucket Relay
                if (scriptMode) {
                    // Validate PDF
                    if (!file.name.toLowerCase().endsWith('.pdf')) {
                        showToast('Please select a PDF file for script import', 'error');
                        continue;
                    }
                    if (!userId) {
                        showToast('Sign in before uploading scripts', 'warning');
                        return;
                    }

                    const session = await createUploadSession(scope, file);
                    await uploadToSession(
                        session,
                        file,
                        (progress) => logger.files(`Script upload ${progress.percent.toFixed(0)}% complete`)
                    );
                    const uploadedFile = await finalizeUpload(scope, {
                        file,
                        session,
                        parentId: 'root',
                        policy: projectWideAccessPolicy(userId),
                    });
                    const storagePath = uploadedFile.storageRef ?? uploadedFile.storagePath ?? session.storagePath;

                    logBetaAnalyticsEvent('beta_file_uploaded', {
                        mime_category: toMimeCategory('application/pdf'),
                        size_bucket: toFileSizeBucket(file.size),
                        access_policy: 'project-wide',
                    });
                    
                    showToast('Script uploaded — starting AI analysis...', 'success');
                    
                    // Start OCR via callable (replaces the old storage trigger)
                    let jobId: string | undefined;
                    try {
                        useAppStore.getState().showStructureActions.setActiveJobId('pending-script-import');
                        const result = await triggerScriptAnalysis(scope, storagePath, {
                            scriptTextAccess: useAppStore.getState().cloud.scriptTextAccess ?? false,
                            importMode: scriptImportMode,
                            analyticsSource: 'upload',
                        });
                        jobId = result.jobId;
                    } catch (ocrError: unknown) {
                        const msg = ocrError instanceof Error ? ocrError.message : 'Unknown error';
                        console.error('Failed to start OCR analysis:', ocrError);
                        useAppStore.getState().showStructureActions.setActiveJobId(null);
                        showToast(`Upload succeeded but OCR failed: ${msg}`, 'error');
                        handleClose();
                        return;
                    }
                    
                    // Call the onScriptUpload callback for job tracking
                    if (onScriptUpload) {
                        onScriptUpload({
                            name: file.name,
                            storagePath,
                            jobId
                        });
                    }
                    
                    handleClose();
                    return; // Script mode only handles one file
                }
                
                // Regular file upload
                const uploadAccessPolicy = !personalMode && userId
                    ? (uploadPolicy ? uploadPolicy(userId) : projectWideAccessPolicy(userId))
                    : undefined;
                const fileId = await filesActions.uploadNewFile(
                    projectId,
                    file,
                    personalMode ? 'root' : currentFolderId,
                    (percent) => logger.files(`Upload ${percent}% complete`),
                    uploadAccessPolicy
                );

                // Reload files based on mode
                if (personalMode && userId) {
                    // Reload personal files
                    const updated = await listPersonalFiles(scope, userId);
                    setPersonalFiles(updated);
                    const uploadedFile = updated.find(f => f.id === fileId);
                    if (uploadedFile) uploadedFiles.push(uploadedFile);
                } else {
                    // Use fresh store state after the upload completes
                    const freshFiles = useAppStore.getState().files;
                    const uploadedFile = freshFiles.find(f => f.id === fileId);
                    if (uploadedFile) {
                        uploadedFiles.push(uploadedFile);
                    }
                }
            }

            showToast(`Uploaded ${uploadedFiles.length} file(s)`, 'success');

            // If single mode, auto-select the uploaded file
            if (mode === 'single' && uploadedFiles.length > 0) {
                onSelect(await resolveFilesForSelection(uploadedFiles));
                handleClose();
            } else {
                // Switch to repository tab to show uploaded files
                setActiveTab('repository');
                setSelectedFileIds(uploadedFiles.map(f => f.id));
            }
        } catch (error: unknown) {
            const msg = error instanceof Error ? error.message : 'Unknown error';
            logger.error('File upload failed:', error);
            showToast(`Upload failed: ${msg}`, 'error');
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose} size="lg">
            <ModalHeader title={title} onClose={handleClose} />

            <div className="flex h-[600px] flex-col">
                {/* Tab Navigation */}
                <div className="flex border-gray-200 border-b dark:border-gray-700">
                    <Button
                        variant="ghost"
                        onClick={() => setActiveTab('repository')}
                        className={`rounded-none border-b-2 px-6 py-3 font-medium text-sm transition-colors ${
                            activeTab === 'repository'
                                ? 'border-primary border-b-2 text-primary'
                                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                        }`}
                    >
                        Select from Repository
                    </Button>
                    <Button
                        variant="ghost"
                        onClick={() => setActiveTab('upload')}
                        className={`rounded-none border-b-2 px-6 py-3 font-medium text-sm transition-colors ${
                            activeTab === 'upload'
                                ? 'border-primary border-b-2 text-primary'
                                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                        }`}
                    >
                        Upload New
                    </Button>
                </div>

                {/* Tab Content */}
                <div className="flex-1 overflow-auto p-4">
                    {activeTab === 'repository' ? (
                        // Repository Tab
                        filteredFiles.length === 0 ? (
                            <EmptyState
                                icon="folder_open"
                                title="No matching files"
                                description={`No ${accept === 'image/*' ? 'image' : accept === 'audio/*' ? 'audio' : ''} files found in repository`}
                                action={
                                    <Button onClick={() => setActiveTab('upload')}>
                                        Upload New File
                                    </Button>
                                }
                            />
                        ) : (
                            <div className={
                                viewMode === 'grid'
                                    ? 'grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4'
                                    : 'space-y-1'
                            }>
                                {filteredFiles.map((file) => {
                                    const isSelected = selectedFileIds.includes(file.id);
                                    const isCached = cachedFileIds.includes(file.id);

                                    return (
                                        <PickerFileItem
                                            key={file.id}
                                            file={file}
                                            isCached={isCached}
                                            isSelected={isSelected}
                                            layout={viewMode}
                                            onSelect={() => handleFileSelect(file.id)}
                                        />
                                    );
                                })}
                            </div>
                        )
                    ) : (
                        // Upload Tab
                        <PickerUploadDropzone
                            onFilesSelected={(fileList) => { void handleUpload(fileList); }}
                            disabled={isUploading}
                        />
                    )}
                </div>

                {/* Footer */}
                {activeTab === 'repository' && (
                    <div className="flex items-center justify-between border-gray-200 border-t p-4 dark:border-gray-700">
                        <div className="text-gray-500 text-sm">
                            {selectedFileIds.length} file(s) selected
                        </div>
                        <div className="flex gap-2">
                            <Button variant="secondary" onClick={handleClose}>
                                Cancel
                            </Button>
                            <Button 
                                variant="primary" 
                                onClick={() => { void handleConfirm(); }}
                                disabled={selectedFileIds.length === 0 || isResolvingSelection}
                            >
                                {isResolvingSelection ? 'Preparing...' : 'Select'}
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </Modal>
    );
};














